import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { fetchShareableUsers, shareLookoutItems } from '../../services/lookoutShareService';

// Reused for both "Share List" (items = every personal Lookout List item)
// and per-row "Share" (items = a single order) - the only difference is
// what the caller passes in for `items`.
const SharePickerModal = ({ items, onClose, onShared }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchShareableUsers()
      .then(list => { if (!cancelled) setUsers(list); })
      .catch(() => { if (!cancelled) toast.error('Failed to load users to share with'); })
      .finally(() => { if (!cancelled) setLoadingUsers(false); });
    return () => { cancelled = true; };
  }, []);

  const filteredUsers = users.filter(user => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (user.UserFullName || '').toLowerCase().includes(term)
      || (user.UserName || '').toLowerCase().includes(term);
  });

  const toggleUser = (username) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(username)) {
        next.delete(username);
      } else {
        next.add(username);
      }
      return next;
    });
  };

  const handleSend = async () => {
    if (selected.size === 0) return;
    setSending(true);
    try {
      await shareLookoutItems(items, Array.from(selected));
      toast.success(
        items.length === 1
          ? `Shared order ${items[0].orderNo} with ${selected.size} ${selected.size === 1 ? 'person' : 'people'}`
          : `Shared ${items.length} orders with ${selected.size} ${selected.size === 1 ? 'person' : 'people'}`
      );
      onShared?.();
      onClose();
    } catch (error) {
      console.error('Error sharing lookout list items:', error);
      toast.error(error.message || 'Failed to share');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="share-picker-overlay" onClick={onClose}>
      <div className="share-picker-modal" onClick={(e) => e.stopPropagation()}>
        <h3>
          <i className="fas fa-share-alt"></i>{' '}
          {items.length === 1 ? `Share Order ${items[0].orderNo}` : `Share List (${items.length} orders)`}
        </h3>

        {loadingUsers ? (
          <p className="share-picker-status">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="share-picker-status">No other active users found.</p>
        ) : (
          <>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users..."
              className="share-picker-search"
              autoFocus
            />
            <div className="share-picker-user-list">
              {filteredUsers.length === 0 ? (
                <p className="share-picker-status">No users match "{searchTerm}".</p>
              ) : (
                filteredUsers.map(user => (
                  <label key={user.UserName} className="share-picker-user-row">
                    <input
                      type="checkbox"
                      checked={selected.has(user.UserName)}
                      onChange={() => toggleUser(user.UserName)}
                    />
                    <span>{user.UserFullName || user.UserName}</span>
                  </label>
                ))
              )}
            </div>
          </>
        )}

        <div className="share-picker-actions">
          <button className="btn-small" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button
            className="btn-small btn-select"
            onClick={handleSend}
            disabled={sending || selected.size === 0}
          >
            {sending ? 'Sending...' : `Share with ${selected.size || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePickerModal;
